import { useEffect, useState } from "react";

import { usePortfolioData } from "@/hooks/usePortfolioData";

export type SortOption = "updated" | "stars";

export type Repository = {
  id: number;
  name: string;
  description: string;
  html_url: string;
  homepage?: string;
  stargazers_count: number;
  forks_count: number;
  language: string;
  topics: string[];
  updated_at: string;
};

interface UseGithubRepositoriesProps {
  refreshTrigger?: number;
}

/**
 * Custom hook for fetching GitHub repositories from the cached backend data
 * @param props - Hook props
 * @param props.refreshTrigger - Optional trigger to refresh the portfolio data
 * @returns Repository data and management functions
 */
export function useGithubRepositories({
  refreshTrigger,
}: UseGithubRepositoriesProps = {}) {
  const { portfolioData, isLoading: portfolioLoading } =
    usePortfolioData(refreshTrigger);
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>("updated");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRepositoriesFromBackend = async (
    githubUsername: string,
    sort: SortOption,
  ): Promise<Repository[]> => {
    try {
      const reposPerPage = portfolioData?.githubSettings?.reposPerPage || 4;
      const response = await fetch(
        `/api/github-repos?username=${encodeURIComponent(githubUsername)}&sort=${sort}&reposCount=${reposPerPage}`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch repositories from backend");
      }

      const data = await response.json();
      return data.repos || [];
    } catch (err) {
      console.error(`Error fetching ${sort} repos from backend:`, err);
      throw err;
    }
  };

  useEffect(() => {
    const loadRepositories = async () => {
      if (portfolioLoading || !portfolioData) {
        setLoading(false);
        return;
      }

      const githubUsername = portfolioData.social?.github;

      if (!githubUsername) {
        setLoading(false);
        setRepositories([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const repos = await fetchRepositoriesFromBackend(githubUsername, sortBy);
        setRepositories(repos);
        setLoading(false);
      } catch (err) {
        setError("Failed to load GitHub repositories");
        setLoading(false);
        console.error(err);
      }
    };

    loadRepositories().catch(console.error);
  }, [
    portfolioData?.social?.github,
    portfolioData?.githubSettings?.reposPerPage,
    portfolioLoading,
    sortBy,
  ]);

  return {
    repositories,
    sortBy,
    setSortBy,
    loading,
    error,
    portfolioData,
    portfolioLoading,
    hasGithubUsername: !!portfolioData?.social?.github,
  };
}